import { useCallback, useEffect, useMemo, useState } from "react";
import { api, STATUS_LABEL, hr, downloadAsBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Send, Stamp, Download, FileSpreadsheet, Eye, Users, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { PdfPreviewDialog } from "../components/PdfPreviewDialog";
import { SecretariatRow } from "../components/SecretariatRow";
import { SecretariatBulkBar } from "../components/SecretariatBulkBar";
import { SecretariatFilters } from "../components/SecretariatFilters";
import { GeneratePdfDialog } from "../components/GeneratePdfDialog";
import { DeleteRequestDialog } from "../components/DeleteRequestDialog";
import { CollectiveIssueDialog } from "../components/CollectiveIssueDialog";
import BehalfVacationDialog from "../components/BehalfVacationDialog";

export default function Secretariat() {
  const { user } = useAuth();
  const [secretaryGeneralName, setSecretaryGeneralName] = useState("");
  // Iter40: default signature je sada IME GLAVNOG TAJNIKA iz Postavki (fallback na user.full_name).
  const defaultSignature = useMemo(
    () => secretaryGeneralName || user?.full_name || "",
    [secretaryGeneralName, user]
  );

  const [vacation, setVacation] = useState([]);
  const [wfh, setWfh] = useState([]);
  const [open, setOpen] = useState(null);
  const [klasa, setKlasa] = useState("");
  const [urbroj, setUrbroj] = useState("");
  // Iter79: opcijski override teksta tijela rješenja (samo za vacation)
  const [customBodyText, setCustomBodyText] = useState("");
  // Iter88: opcijski datum izrade rješenja (za povijesne odluke unesene retroaktivno)
  const [decisionIssuedAt, setDecisionIssuedAt] = useState("");
  // Iter89: rješenje "po povratku" — bez fiksnog perioda i bez oduzimanja fonda
  const [deferPeriod, setDeferPeriod] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [filterKatedra, setFilterKatedra] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Iter46: bulk collective issuance dialog
  const [collectiveOpen, setCollectiveOpen] = useState(false);
  // Iter52: pojedinačno GO rješenje u ime zaposlenika (s embedded employee pickerom)
  const [behalfOpen, setBehalfOpen] = useState(false);
  // Iter42: email → excel_rb map za auto-generaciju URBROJ-a za godišnji odmor.
  const [excelRbByEmail, setExcelRbByEmail] = useState({});

  const load = useCallback(async () => {
    const [v, w] = await Promise.all([
      api.get("/vacation/requests").then(r => r.data),
      api.get("/wfh/requests").then(r => r.data),
    ]);
    setVacation(v); setWfh(w);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Iter42: dohvati svi users i mapiraj email → excel_rb za auto-URBROJ.
  useEffect(() => {
    api.get("/users")
      .then(r => {
        const map = {};
        for (const u of r.data) {
          if (u.excel_rb) map[u.email] = u.excel_rb;
        }
        setExcelRbByEmail(map);
      })
      .catch(() => {});
  }, []);

  // Iter42: helper za auto-generiranje KLASA/URBROJ pri otvaranju dialoga.
  const openGenerateDialog = (item, type) => {
    setOpen({ item, type });
    if (type === "vacation") {
      // KLASA fiksna za sve godišnje odmore
      setKlasa("113-02/26-01/001");
      // URBROJ: 251-75-03-{YY}-{NNN} gdje NNN = excel_rb (3 znamenke).
      // Godina se uzima iz current request-a (created_at), fallback na ovu godinu.
      const yyFromReq = item.created_at ? new Date(item.created_at).getFullYear() : new Date().getFullYear();
      const yy = String(yyFromReq).slice(-2);
      const rb = excelRbByEmail[item.employee_email];
      if (rb) {
        setUrbroj(`251-75-03-${yy}-${String(rb).padStart(3, "0")}`);
      } else {
        setUrbroj("");
      }
    } else {
      setKlasa("");
      setUrbroj("");
    }
  };

  // Iter40: dohvati ime glavnog tajnika iz Postavki za pre-fill u Preview dijalogu.
  useEffect(() => {
    api.get("/settings")
      .then(r => setSecretaryGeneralName(r.data?.secretary_general_name || ""))
      .catch(() => {});
  }, []);

  const allKatedras = useMemo(() => {
    const items = [...vacation, ...wfh].map(r => r.employee_katedra).filter(k => !!k);
    return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b, "hr"));
  }, [vacation, wfh]);

  const applyFilters = useCallback((rows) => {
    const q = search.trim().toLowerCase();
    const fromTs = filterFrom ? new Date(`${filterFrom}T00:00:00`).getTime() : null;
    const toTs = filterTo ? new Date(`${filterTo}T23:59:59`).getTime() : null;
    const filtered = rows.filter(r => {
      if (filterKatedra !== "all" && r.employee_katedra !== filterKatedra) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (fromTs || toTs) {
        const refIso = r.sent_at || r.pdf_generated_at || r.manager_decided_at || r.created_at;
        if (!refIso) return false;
        const t = new Date(refIso).getTime();
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }
      if (q) {
        const hay = `${r.employee_name} ${r.employee_email} ${r.employee_katedra || ""} ${r.klasa || ""} ${r.urbroj || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return filtered.sort((a, b) => {
      const ta = new Date(a.sent_at || a.pdf_generated_at || a.manager_decided_at || a.created_at || 0).getTime();
      const tb = new Date(b.sent_at || b.pdf_generated_at || b.manager_decided_at || b.created_at || 0).getTime();
      return sortDir === "desc" ? tb - ta : ta - tb;
    });
  }, [search, filterKatedra, filterStatus, filterFrom, filterTo, sortDir]);

  const clearFilters = () => {
    setSearch(""); setFilterKatedra("all"); setFilterStatus("all");
    setFilterFrom(""); setFilterTo("");
  };
  const hasFilters = !!search || filterKatedra !== "all" || filterStatus !== "all" || !!filterFrom || !!filterTo;

  const baseFor = (type) => type === "wfh" ? "/wfh/requests" : "/vacation/requests";

  const generate = async () => {
    if (!open) return;
    setBusy(true);
    try {
      // Iter79: pošalji custom_body_text samo za vacation kad je user editirao
      const body = { klasa, urbroj };
      if (open.type === "vacation") {
        body.custom_body_text = customBodyText || null;
        // Iter88: opcijski datum izrade rješenja (retroaktivno)
        body.decision_issued_at = decisionIssuedAt || null;
        // Iter89: opcijski flag "po povratku" (bez perioda + bez oduzimanja fonda)
        body.defer_period = !!deferPeriod;
      }
      await api.post(`${baseFor(open.type)}/${open.item.id}/generate-pdf`, body);
      toast.success("Rješenje generirano");
      openPreview({ id: open.item.id, type: open.type, employee_name: open.item.employee_name });
      setOpen(null); setKlasa(""); setUrbroj(""); setCustomBodyText(""); setDecisionIssuedAt(""); setDeferPeriod(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
    finally { setBusy(false); }
  };

  const openPreview = ({ id, type, employee_name }) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const path = type === "wfh" ? "wfh" : "vacation";
    const pdfUrl = `${base}/api/${path}/requests/${id}/pdf?ts=${Date.now()}`;
    setPreview({ id, type, employee_name, pdfUrl });
  };
  const closePreview = useCallback(() => setPreview(null), []);

  const sendDecision = async (id, type) => {
    try {
      const r = await api.post(`${baseFor(type)}/${id}/send`);
      toast.success(`Rješenje izrađeno i poslano na ${r.data.recipients.join(", ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  const downloadPdf = (id, type) => {
    downloadAsBlob(`${baseFor(type)}/${id}/pdf`, `Odluka_${type === "wfh" ? "WFH" : "GO"}_${id.slice(0,6)}.pdf`);
  };

  const doDeleteRequest = async () => {
    if (!deleteFor) return;
    setDeleteBusy(true);
    const isFinal = ["decided", "sent"].includes(deleteFor.status);
    const url = `${baseFor(deleteFor.type)}/${deleteFor.id}${isFinal ? "?force=true" : ""}`;
    try {
      await api.delete(url);
      toast.success(`Zahtjev obrisan${isFinal ? " (uz force)" : ""}`);
      setDeleteFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
    finally { setDeleteBusy(false); }
  };

  const downloadXlsx = async (kind) => {
    const FILENAMES = { employees: "Zaposlenici.xlsx", vacation: "GO_2026.xlsx", wfh: "WFH_2026.xlsx" };
    try {
      await downloadAsBlob(`/export/${kind}.xlsx`, FILENAMES[kind] || `${kind}.xlsx`);
      toast.success("Preuzeto");
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const clearSelection = () => setSelected({});
  const selectedIds = (rows) => rows.filter(r => selected[r.id]).map(r => r.id);

  const bulk = async (type, action, ids, confirmMsg) => {
    if (ids.length === 0) { toast.error("Niste odabrali nijedan zahtjev"); return; }
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`${baseFor(type)}/bulk-${action}`, { ids });
      toast.success(r.data.summary);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
    finally { setBulkBusy(false); }
  };

  const renderSection = (items, type) => {
    const approved = applyFilters(items.filter(r => r.status === "approved"));
    const decided = applyFilters(items.filter(r => r.status === "decided"));
    const sent = applyFilters(items.filter(r => r.status === "sent"));
    const onSelectAll = (rows, checked) => {
      if (checked) {
        const next = { ...selected };
        rows.forEach(i => { next[i.id] = true; });
        setSelected(next);
      } else clearSelection();
    };
    return (
      <Tabs defaultValue="approved" className="mt-3" onValueChange={() => clearSelection()}>
        <TabsList>
          <TabsTrigger value="approved" data-testid={`tab-${type}-approved`}>Za izdavanje rješenja ({approved.length})</TabsTrigger>
          <TabsTrigger value="decided" data-testid={`tab-${type}-decided`}>U izradi rješenja ({decided.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid={`tab-${type}-sent`}>Rješenje izrađeno ({sent.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="approved" className="space-y-3 mt-4">
          {approved.length === 0
            ? <p className="text-muted-foreground text-sm">Nema odobrenih zahtjeva čekajućih rješenje.</p>
            : <>
              <SecretariatBulkBar
                items={approved} type={type} action="generate"
                label={`Generiraj ${selectedIds(approved).length || ""} odabranih`}
                selectedIds={selectedIds}
                onSelectAll={(checked) => onSelectAll(approved, checked)}
                onBulk={bulk}
                bulkBusy={bulkBusy}
              />
              {approved.map(r => (
                <SecretariatRow
                  key={r.id} r={r} type={type} selectable
                  selected={selected} onToggle={toggle}
                  onDelete={(p) => setDeleteFor(p)}
                  action={
                    <Button size="sm" onClick={() => openGenerateDialog(r, type)} data-testid={`gen-pdf-${r.id}`}>
                      <Stamp className="w-4 h-4 mr-1" /> Generiraj rješenje
                    </Button>
                  }
                />
              ))}
            </>}
        </TabsContent>
        <TabsContent value="decided" className="space-y-3 mt-4">
          {decided.length === 0
            ? <p className="text-muted-foreground text-sm">Nema rješenja za slanje.</p>
            : <>
              <SecretariatBulkBar
                items={decided} type={type} action="send"
                label={`Pošalji ${selectedIds(decided).length || ""} odabranih`}
                selectedIds={selectedIds}
                onSelectAll={(checked) => onSelectAll(decided, checked)}
                onBulk={bulk}
                bulkBusy={bulkBusy}
              />
              {decided.map(r => (
                <SecretariatRow
                  key={r.id} r={r} type={type} selectable
                  selected={selected} onToggle={toggle}
                  onDelete={(p) => setDeleteFor(p)}
                  action={<>
                    <Button size="sm" variant="outline" onClick={() => openPreview({id: r.id, type, employee_name: r.employee_name})} data-testid={`preview-${r.id}`}><Eye className="w-4 h-4 mr-1"/>Pregled</Button>
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, type)} data-testid={`download-${r.id}`}><Download className="w-4 h-4 mr-1"/>PDF</Button>
                    <Button size="sm" onClick={() => sendDecision(r.id, type)} data-testid={`send-${r.id}`}><Send className="w-4 h-4 mr-1"/>Pošalji</Button>
                  </>}
                />
              ))}
            </>}
        </TabsContent>
        <TabsContent value="sent" className="space-y-3 mt-4">
          {sent.length === 0
            ? <p className="text-muted-foreground text-sm">Još nema poslanih rješenja.</p>
            : sent.map(r => (
              <SecretariatRow
                key={r.id} r={r} type={type}
                selected={selected} onToggle={toggle}
                onDelete={(p) => setDeleteFor(p)}
                action={<Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, type)}><Download className="w-4 h-4 mr-1"/>PDF</Button>}
              />
            ))}
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <div className="space-y-6" data-testid="secretariat-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="serif text-3xl sm:text-4xl font-semibold">Tajništvo dekanata</h1>
          <p className="text-muted-foreground mt-1">Generiranje rješenja s potpisom dekana i slanje zaposleniku · SharePoint</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => setCollectiveOpen(true)} data-testid="open-collective-dialog">
            <Users className="w-4 h-4 mr-1"/>Izdaj kolektivno rješenje
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setBehalfOpen(true)} data-testid="open-behalf-dialog">
            <UserPlus className="w-4 h-4 mr-1"/>Izradi pojedinačno rješenje
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadXlsx("employees")} data-testid="xlsx-employees">
            <FileSpreadsheet className="w-4 h-4 mr-1"/>Zaposlenici.xlsx
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadXlsx("vacation")} data-testid="xlsx-vacation">
            <FileSpreadsheet className="w-4 h-4 mr-1"/>GO 2026.xlsx
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadXlsx("wfh")} data-testid="xlsx-wfh">
            <FileSpreadsheet className="w-4 h-4 mr-1"/>WFH 2026.xlsx
          </Button>
        </div>
      </div>

      <SecretariatFilters
        allKatedras={allKatedras}
        search={search} onSearchChange={setSearch}
        filterKatedra={filterKatedra} onKatedraChange={setFilterKatedra}
        filterStatus={filterStatus} onStatusChange={setFilterStatus}
        filterFrom={filterFrom} onFromChange={setFilterFrom}
        filterTo={filterTo} onToChange={setFilterTo}
        sortDir={sortDir}
        onSortToggle={() => setSortDir(d => d === "desc" ? "asc" : "desc")}
        hasFilters={hasFilters}
        onClear={clearFilters}
      />

      <Tabs defaultValue="vacation" onValueChange={() => clearSelection()}>
        <TabsList>
          <TabsTrigger value="vacation" data-testid="tab-sec-vacation">Godišnji odmor</TabsTrigger>
          <TabsTrigger value="wfh" data-testid="tab-sec-wfh">Rad od kuće</TabsTrigger>
        </TabsList>
        <TabsContent value="vacation">{renderSection(vacation, "vacation")}</TabsContent>
        <TabsContent value="wfh">{renderSection(wfh, "wfh")}</TabsContent>
      </Tabs>

      <GeneratePdfDialog
        open={open}
        onClose={() => { setOpen(null); setKlasa(""); setUrbroj(""); setCustomBodyText(""); setDecisionIssuedAt(""); setDeferPeriod(false); }}
        klasa={klasa} urbroj={urbroj}
        onKlasaChange={setKlasa}
        onUrbrojChange={setUrbroj}
        onGenerate={generate}
        busy={busy}
        customBodyText={customBodyText}
        onCustomBodyChange={setCustomBodyText}
        decisionIssuedAt={decisionIssuedAt}
        onDecisionIssuedAtChange={setDecisionIssuedAt}
        deferPeriod={deferPeriod}
        onDeferPeriodChange={setDeferPeriod}
      />

      <DeleteRequestDialog
        deleteFor={deleteFor}
        onCancel={() => setDeleteFor(null)}
        onConfirm={doDeleteRequest}
        busy={deleteBusy}
      />

      <PdfPreviewDialog
        preview={preview}
        defaultSignature={defaultSignature}
        onClose={closePreview}
        onSend={async (preparedBy) => {
          if (!preview) return;
          try {
            const r = await api.post(`${baseFor(preview.type)}/${preview.id}/send`, { prepared_by: preparedBy || null });
            toast.success(`Rješenje izrađeno i poslano na ${r.data.recipients.join(", ")}`);
            closePreview();
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Greška pri slanju");
          }
        }}
        onDownload={downloadPdf}
      />

      <CollectiveIssueDialog
        open={collectiveOpen}
        onClose={() => setCollectiveOpen(false)}
        onIssued={load}
      />

      {/* Iter52: pojedinačno GO rješenje u ime zaposlenika */}
      <BehalfVacationDialog
        pickEmployee
        open={behalfOpen}
        onClose={() => setBehalfOpen(false)}
        onSuccess={load}
      />
    </div>
  );
}
